import React from 'react';
import { useTranslation } from 'react-i18next';

import { publish, SubscriptionTopic } from '../../../../../../viewer/messaging/pubsub';
import { Button } from '../../../../button/button';
import { Dialog } from '../../../../dialog/dialog';
import { Icon } from '../../../../icon/icon';
import { Stack } from '../../../../stack/stack';
import { CadDrawingReportExportStatus } from './cad-drawing-report-export-status';
import { cadDrawingReportExport, isCollectingScene, isExportRunning } from './report-export';

type Props = {
  onClose: () => void;
};

/**
 * Follows the CAD drawing report's export. It can't be closed while the scene is read, so it
 * doesn't change. After that the user can wait for the PDF here, or close it and be told in the
 * notification center.
 */
export const CadDrawingReportExportDialog = ({ onClose }: Props) => {
  const { t } = useTranslation();
  const state = React.useSyncExternalStore(
    cadDrawingReportExport.subscribe,
    cadDrawingReportExport.getState,
  );
  const collecting = isCollectingScene(state);
  const running = isExportRunning(state);
  // The preview takes most of the screen, to be read without downloading the PDF.
  const showsPreview = state.status === 'completed' && !!state.thumbnailUrl;

  const close = () => {
    if (collecting) {
      return;
    }

    if (running) {
      cadDrawingReportExport.detach();
      publish(SubscriptionTopic.ToastrMessage, {
        message: t('cadDrawingReport.export.notifiedWhenDone', { ns: 'skyviewReport' }),
        type: 'info',
      });
    }
    onClose();
  };

  return (
    <Dialog
      closeIcon={!collecting}
      closeOnDimmerClick={false}
      maxHeight={showsPreview ? 'expand' : true}
      width={showsPreview ? 'fit-content' : 420}
      onClose={close}
    >
      {{
        header: t('cadDrawingReport.export.dialogHeader', { ns: 'skyviewReport' }),
        content: (
          <Stack spacing={1}>
            {running && (
              <Stack alignItems="center" direction="row" spacing={0.75}>
                <Icon icon={['fad', 'spinner']} size="2x" spin={true} />
                <span>
                  {collecting
                    ? t('cadDrawingReport.export.keepView', { ns: 'skyviewReport' })
                    : t('cadDrawingReport.export.canClose', { ns: 'skyviewReport' })}
                </span>
              </Stack>
            )}
            <CadDrawingReportExportStatus state={state} />
          </Stack>
        ),
        footer: {
          right: (
            <Button disabled={collecting} variant="text" onClick={close}>
              {t('close', { ns: 'common' })}
            </Button>
          ),
        },
      }}
    </Dialog>
  );
};
